import {Column, FilterFn} from '@tanstack/react-table'
import {CSSProperties, Dispatch, SetStateAction} from 'react'
import {
  customNumberFormat,
  getDataById,
  getDateRangeDuration,
  getDurationDay,
} from '../../../functions/general'
import {calculateDayDurations} from '../../../functions/calculateDuration'
import moment from 'moment-timezone'
import {CustomeStoreCompanyIdProps, useCustomeStore} from '../../../stores/CustomeStore'
import {Pipeline} from '../../../interfaces'
import {SettingMultiMerchants, SettingMultiPipelines, Settings} from '../../../interfaces/Settings'
import {Merchant} from '../../../interfaces/Merchant'
import {UserModel} from '../../../app/modules/auth/models/UserModel'
import {User} from '../../../interfaces/User'
import {FilterConfigTanstack} from '../MasterDataTableTanstack'
import { checkIsCompanyTanrise, checkIsNewProgress } from '../../../functions/customCase'

// ============================================================== Sort Data ==============================================================
export interface HandleFormatDataProps {
  tableKey: string
  data: any
  orderBy: string
  orderType: 'ASC' | 'DESC' | undefined
  setLoading: Dispatch<SetStateAction<boolean>> | undefined
  intl: any
  user: UserModel
  companyId: CustomeStoreCompanyIdProps
  selectMultiPipeline: SettingMultiPipelines
  selectMultiMerchant: SettingMultiMerchants
  pipelines: Pipeline[]
  merchants: Merchant[]
  users: User[]
  settings: Settings
}

export const handleFormatData = async ({
  tableKey,
  data,
  orderBy,
  orderType,
  setLoading,
  intl,
  user,
  companyId,
  selectMultiPipeline,
  selectMultiMerchant,
  pipelines,
  merchants,
  users,
  settings,
}: HandleFormatDataProps): Promise<{tableKey: string; sortedData: any[]} | undefined> => {
  let dataMain = []
  if (tableKey === 'users-performance') {
    dataMain = [...data.usersPerformance]
    // setTablesData(tableKey, data.usersPerformance)
  } else if (tableKey === 'pending-leads') {
    dataMain = [...data.pendingPipelines]
    // setTablesData(tableKey, data.pendingPipelines)
  } else if (tableKey === 'paid-leads') {
    dataMain = [...data.paidPipelines]
    // setTablesData(tableKey, data.succeedPipelines)
  } else if (tableKey === 'sold-product-leads') {
    dataMain = [...data.productsSold]
    // setTablesData(tableKey, data.productsSold)
  } else if (tableKey === 'merchants') {
    const merchants = data.map((eachData: any) => ({
      ...eachData,
      lastMeetEnd: eachData.lastMeetEnd ? getDurationDay(eachData.lastMeetEnd) : '-',
      lastLeadCreatedTime: eachData.lastLeadCreatedTime
        ? getDurationDay(eachData.lastLeadCreatedTime)
        : '-',
      // dateCreated: moment(eachData.dateCreated).format("DD MMM YYYY HH:mm")
    }))

    dataMain = [...merchants]
    // setTablesData(tableKey, merchants)
  } else if (tableKey === 'logs') {
    var mergeData: any[] = []
    mergeData = data

    // const sortedLeadData = mergeData.sort((a: Pipeline, b: Pipeline) => {
    //     return new Date(a.date_created).getTime() - new Date(b.date_created).getTime();
    // });

    if (user.data.company_id === companyId.juraganMaterial) {
      mergeData.forEach((eachData: any, index: number) => {
        if (index !== 0) {
          eachData.duration = getDateRangeDuration(
            mergeData[index].date_created,
            mergeData[index - 1].date_created
          )
        } else {
          eachData.duration = '-'
        }
      })
    } else {
      mergeData.forEach((eachData: any, index: number) => {
        if (index < mergeData.length - 1) {
          if (user.data.company_id === companyId.esb) {
            eachData.duration = calculateDayDurations(
              mergeData[index + 1].date_created,
              mergeData[index].date_created
            )
          } else {
            eachData.duration = getDateRangeDuration(
              mergeData[index + 1].date_created,
              mergeData[index].date_created
            )
          }
        } else {
          eachData.duration = '-'
        }
      })
    }

    // sortedLeadData.sort((a: Pipeline, b: Pipeline) => {
    //     return new Date(b.date_created).getTime() - new Date(a.date_created).getTime();
    // });

    dataMain = [...mergeData]
    // setTablesData(tableKey, mergeData)
  } else if (tableKey === 'pipelines') {
    const newProgress = checkIsCompanyTanrise(companyId, user) ? intl.formatMessage({ id: "FORM.LABEL.CONTACTED" }) : intl.formatMessage({ id: "FORM.LABEL.NEW" })
    const products = data.map((eachData: any) => ({
      ...eachData,
      leadLabelProducts: eachData.leadProducts
        .map((productLabel: any) => productLabel.productLabels)
        .join(', '),
      leadNameProducts: eachData.leadProducts
        .map((productLabel: any) => productLabel.productName)
        .join(', '),
      lead_progress_name: checkIsNewProgress(eachData.lead_progress_name) ? newProgress : eachData.lead_progress_name,
    }))

    dataMain = [...products]
    // setTablesData(tableKey, products)
  } else if (tableKey === 'meetings') {
    const meetings = data.map((eachData: any) => ({
      ...eachData,
      status:
        eachData.status === 'PLANNED'
          ? intl.formatMessage({id: 'MEETING.STATUS.SCHEDULED'})
          : eachData.status === 'COMPLETED'
          ? intl.formatMessage({id: 'MEETING.STATUS.FINISHED'})
          : eachData.status === 'ONGOING'
          ? intl.formatMessage({id: 'MEETING.STATUS.ONGOING'})
          : intl.formatMessage({id: 'MEETING.STATUS.CANCELLED'}),
      date_meet: moment(eachData.date_meet).format('YYYY-MM-DD HH:mm:ss'),
      date_meet_end:
        eachData.date_meet_end === null
          ? null
          : eachData.allDay
          ? moment(eachData.date_meet_end).subtract(1, 'days').local().format('YYYY-MM-DD HH:mm:ss')
          : moment(eachData.date_meet_end).format('YYYY-MM-DD HH:mm:ss'),
      duration:
        eachData.date_meet_end === null
          ? '-'
          : getDateRangeDuration(eachData.date_meet, eachData.date_meet_end),
    }))

    dataMain = [...meetings]
    // setTablesData(tableKey, meetings)
  } else if (tableKey === 'units') {
    let defaultUnit = {
      name: 'PIECES',
      alias: 'PCS',
    }

    const mergeData = [...data, defaultUnit]

    dataMain = [...mergeData]
    // setTablesData(tableKey, mergeData)
  } else if (tableKey === 'reimbursement-limits') {
    const limits: any = []
    data.forEach((eachData: any) => {
      eachData.limits.forEach((limit: any) => {
        limits.push({
          userId: eachData.id,
          userName: eachData.name,
          ...limit,
        })
      })
    })
    dataMain = [...limits]
    // setTablesData(tableKey, limits)
  } else if (tableKey === 'roles') {
    const sortData = data.sort((a: any, b: any) => {
      return new Date(b.date_created).getTime() - new Date(a.date_created).getTime();
    });

    dataMain = [...sortData]
    // setTablesData(tableKey, data)
  } else {
    dataMain = [...data]
    // setTablesData(tableKey, data)
  }

  if (setLoading) {
    setLoading(false)
  }

  // Inisialisasi custom fields
  const dataCustomFieldPipeline =
    selectMultiPipeline && Object.keys(selectMultiPipeline).length !== 0
      ? selectMultiPipeline.customFields
      : {}
  const dataCustomFieldMeeting = settings.meeting_custom_fields
    ? JSON.parse(settings.meeting_custom_fields)
    : {}
  const dataCustomFieldMerchant =
    selectMultiMerchant && Object.keys(selectMultiMerchant).length !== 0
      ? selectMultiMerchant.customFields
      : {}

  try {
    // Format data menggunakan Promise.all
    const flattenedData = await Promise.all(
      dataMain.map(async (data: any) => {
        for (const field in data) {
          if (field === 'custom_fields' || field === 'customFields') {
            const customFields = data['custom_fields'] ?? data['customFields']
            for (const key in customFields) {
              if (Array.isArray(customFields[key])) {
                // Logika untuk array
                if (
                  (tableKey === 'pipelines' && dataCustomFieldPipeline[key]?.type === 'serial') ||
                  (tableKey === 'meetings' && dataCustomFieldMeeting[key]?.type === 'serial') ||
                  (tableKey === 'merchants' && dataCustomFieldMerchant[key]?.type === 'serial')
                ) {
                  data[`custom_fields.${key}`] = customFields[key].join('')
                } else if (
                  dataCustomFieldPipeline[key]?.type === 'merchant' ||
                  dataCustomFieldMerchant[key]?.type === 'merchant'
                ) {
                  data[`custom_fields.${key}`] = getDataById(customFields[key], merchants)
                    ?.map((item: any) => item.name)
                    .join(', ')
                } else if (dataCustomFieldPipeline[key]?.type === 'user') {
                  data[`custom_fields.${key}`] = getDataById(customFields[key], users)
                    ?.map((item: any) => item.name)
                    .join(', ')
                } else {
                  data[`custom_fields.${key}`] = customFields[key].join(', ')
                }
              } else {
                // Logika untuk non-array
                if (dataCustomFieldPipeline[key]?.type === 'pipeline') {
                  const pipelineData = getDataById(customFields[key], pipelines)
                  data[`custom_fields.${key}`] = pipelineData
                    ? `${pipelineData.name} / Rp.${customNumberFormat(
                        pipelineData.nominal
                      )} / ${moment(pipelineData.dateCreated).format('DD MMM YYYY')}`
                    : '-'
                } else {
                  data[`custom_fields.${key}`] = customFields[key]
                }
              }
            }
          }
        }
        return data // Return hasil format
      })
    )

    // Validasi hasil dan set tabel data
    if (Array.isArray(flattenedData)) {
      let sortedData = [...flattenedData]

      if (orderBy && orderType) {
        sortedData.sort((a, b) => {
          const valueA = a[orderBy]
          const valueB = b[orderBy]

          const isDate = Date.parse(valueA) && Date.parse(valueB)
          const parsedValueA = isDate ? new Date(valueA) : valueA
          const parsedValueB = isDate ? new Date(valueB) : valueB

          if (parsedValueA < parsedValueB) return orderType === 'ASC' ? -1 : 1
          if (parsedValueA > parsedValueB) return orderType === 'ASC' ? 1 : -1
          return 0
        })
      }

      //   setTablesData(tableKey, sortedData)
      //   setFilteredTablesData(tableKey, sortedData)
      return {
        tableKey: tableKey,
        sortedData: sortedData,
      }
    } else {
      console.error('Flattened data kosong atau tidak valid.')
    }
  } catch (error) {
    console.error('Terjadi kesalahan saat memproses data:', error)
  }
}

// ------------------------------------------------------------ Dashboard Performance Utils ------------------------------------------------------------
export const calculateTotal = (rows: any[], columnId: string) => {
    return rows.reduce((total, row) => {
      const value = row.hasOwnProperty(columnId) ? row.getValue(columnId) : 0
      return total + (value !== undefined && value !== null ? value : 0)
    }, 0)
}

// ------------------------------------------------------------ Reimbursement Limit Utils (Fairus) ------------------------------------------------------------

type RowData = {
  userName: string
  roleName: string
  [key: string]: string // Allows any string key, such as year_2024, month_1_2024, etc.
}

export const getMonthsForYear = (data: RowData, year: string) => {
  return Object.keys(data)
    .filter((key) => key.startsWith(`month_`) && key.includes(year))
    .map((key) => key.split('_')[1])
}

// ------------------------------------------------------------ Pinning Col Styles ------------------------------------------------------------
export const getCommonPinningStyles = (column: Column<any>): CSSProperties => {
  //   if(tableKey === 'reimbursement-limits') return {}
  const isPinned = column.getIsPinned()
  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left')
  const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right')

  // console.log(column.id, column.getSize())
  return {
    backgroundColor: '#FFF',
    borderBottom: isPinned ? '1px solid #E4E6EF' : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    opacity: isPinned ? 0.95 : 1,
    position: isPinned ? 'sticky' : 'static',
    // width: column.getSize(),
    width: isPinned
      ? '30px'
      : (column.columnDef.meta as any)?.styleBody?.width
      ? (column.columnDef.meta as any)?.styleBody?.width
      : 'auto',
    minWidth: isPinned
      ? '30px'
      : (column.columnDef.meta as any)?.styleBody?.minWidth
      ? (column.columnDef.meta as any)?.styleBody?.minWidth
      : '80px',
    zIndex: isPinned ? 1 : 0,
  }
}

// ------------------------------------------------------------ Search Filter Config ------------------------------------------------------------
export const customGlobalFilter = (filterConfig: FilterConfigTanstack | undefined) => {
//   console.log('filterConfig', filterConfig)
  const result: FilterFn<any> = (row, columnId, filterValue) => {
    // jika columnId ada di dalam excludeList maka akan mengembalikan nilai true
    // artinya data tidak akan di filter berdasarkan nilai filterValue
    if (filterConfig?.excludeGlobalFilter?.includes(columnId)) return true
    return String(row.getValue(columnId)).toLowerCase().includes(String(filterValue).toLowerCase())
  }
  return result
}
